'use client';
import { ChangeEvent, useState } from 'react';

import { useForm } from '@/hooks/useForm';

import { cmsApiEndpoint } from '@/constant/env';
import { EMAIL_REGEX } from '@/utils/validation';
import { apiCall } from '@/utils/fetch';

import { CaptureUserDetailsProps } from '@/components/organisms/GatedContent/types';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { getSanitizedGateKey, setGateCleared } from './utils';

export type CaptureUserOptions = {
  context?: 'leadPopUp' | 'default';
  onSuccess?: () => void;
  onError?: () => void;
};

export type CaptureUserDetailsState = {
  email?: string;
  firstName?: string;
  lastName?: string;
  message?: string;
};

const useCaptureUserDetails = (
  props: CaptureUserDetailsProps,
  options?: CaptureUserOptions
) => {
  const { texts } = useTranslation();

  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const gateId = getSanitizedGateKey(props.gateIdentifier);

  const form = useForm<CaptureUserDetailsState>({
    initialValues: { email: '', firstName: '', lastName: '', message: '' },
    validateOnChange: false,
    validateOnBlur: false,
    validator: {
      email: (value) => {
        if (!value) return texts.submission_status_message.required;
        if (typeof value !== 'string' || !EMAIL_REGEX.test(value)) {
          return texts.gated_content.please_provide_a_valid_email_address;
        }
      },
      firstName: (value) => {
        if (props.isNameFieldVisible && !value)
          return texts.submission_status_message.required;
      },
    },
  });

  const { handleSubmit: onFormSubmit, setFieldValue, values } = form;

  const handleSubmit = async () => {
    setApiError(null);

    const isEmailRequired = true;
    const isFirstNameRequired = props.isNameFieldVisible;
    const missingRequired =
      (isEmailRequired && (!values.email || form.errors.email)) ||
      (isFirstNameRequired && !values.firstName);

    if (missingRequired) {
      setApiError(texts.submission_status_message.please_fill_required_fields);
      setIsLoading(false);
      return false;
    }

    setIsLoading(true);

    try {
      const response = await apiCall<any>({
        url: cmsApiEndpoint + '/users/capture_details',
        method: 'POST',
        body: {
          activeCampaignListId: props.activeCampaignListId,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          message: values.message,
          saveExistingEmails: props.saveExistingEmails,
        },
      });

      setIsLoading(false);

      if (!response || response?.error) {
        const errorMessage =
          typeof response?.error === 'string'
            ? response.error
            : texts.gated_content.default_error_message;
        setApiError(errorMessage);

        if (options?.context === 'leadPopUp' && options?.onError) {
          options.onError();
        }
        return false;
      }

      if (options?.context !== 'leadPopUp') {
        setGateCleared(props.gateIdentifier);
      }

      if (options?.context === 'leadPopUp' && options?.onSuccess) {
        options.onSuccess();
      }
      return true;
    } catch (error: any) {
      setIsLoading(false);
      setApiError(error?.message || texts.gated_content.default_error_message);
      console.error(error);

      if (options?.context === 'leadPopUp' && options?.onError) {
        options.onError();
      }
      return false;
    }
  };

  const getSetFieldValue =
    (name: keyof CaptureUserDetailsState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFieldValue(name, e.target.value);

  return {
    ...form,
    errors: {
      email: form.errors?.email || apiError || undefined,
      firstName: form.errors?.firstName,
      lastName: form.errors?.lastName,
      message: form.errors?.message,
    },
    gateId,
    isLoading,
    wasSubmitted: form.hasSubmitted,
    handleSubmit,
    getSetFieldValue,
    onFormSubmit,
  };
};

export default useCaptureUserDetails;
